import { useState, useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { useAppDispatch } from 'hooks/reduxHooks'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

import ConfettiExplosion from 'react-confetti-explosion'
import { fetchOfferById } from 'actions/OfferActions'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import useHasMounted from 'hooks/useHasMounted'
import WheelOfPrize from '../WheelOfPrize/WheelOfPrize'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'

const Root = styled(VerticalSpacer)`
  z-index: 1;

  &:nth-child(even) {
    background: ${props => props.theme.palette.neutral.default.eight};
  }

  padding: ${rem(40)} ${rem(40)} 0 ${rem(40)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(60)} ${rem(60)} 0 ${rem(60)};
  }

  & .slider-list{
    align-items: center;
  }
`

const ConfettiWrapper = styled.div`
  z-index: -1;
  width: 100%;
  display: flex;
  justify-content: center;
  position: absolute;
  top: 28%;
  overflow: hidden;
  height: 50%;
`

export interface Props {
  openModalMinorPrizes: () => void
  prize?: any
  offerId: string
  announcedTime?: string
  promo_link?: string
  memberId?: string
  competitionTitle: string
  competitionHeader: string
  bodyText?: string
  brandTitle: string
  fetchPrize: any
  breadcrumb: string
  showPrizes?: boolean
  drawDate: string
  isModalOpen: boolean
  closeModal: () => void
  regionCode: string
  footerText?: string
}

function Competition(props: Props) {
  const {
    prize,
    promo_link,
    memberId,
    offerId,
    competitionTitle,
    fetchPrize,
    breadcrumb,
    drawDate,
    closeModal,
    isModalOpen,
    competitionHeader,
    bodyText,
    footerText,
  } = props

  const intl = useIntl()
  const mounted = useHasMounted()

  const SHOW_NOTIFICATION_DELAY = 1000

  const [isAnimationFinished, setAnimationFinished] = useState<boolean>(false)
  const dispatch = useAppDispatch()

  const isRewardReceived = useMemo(() => isAnimationFinished && prize?.prizeValue, [isAnimationFinished, prize?.prizeValue])

  useEffect(() => {
    dispatch(fetchOfferById(offerId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (prize?.numberOfTicketsWon && isAnimationFinished) {
      const snackbarMessage = intl.formatMessage(
        {
          id: '9xU01ztxKUJN4qzZ0uRz',
          defaultMessage: 'Congratulations! You’ve won {entries} entries. Come back tomorrow to play again and win more entries!',
          description: 'Success snackbar message after winning competition entries',
        },
        { entries: prize?.prizeValue },
      )
      const snackbarHeading = intl.formatMessage({
        id: 'C2qjfBX9CP2Uti-Wek2m',
        defaultMessage: 'Congratulations on your win!',
        description: 'Section heading for competition win snackbar notification',
      })
      setTimeout(() => {
        showSnackbar(
          snackbarMessage,
          'success',
          { heading: snackbarHeading },
        )
      }, SHOW_NOTIFICATION_DELAY)
    }
  }, [prize, isAnimationFinished, dispatch, SHOW_NOTIFICATION_DELAY, intl])

  if (!mounted) {
    return null
  }

  return (
    <Root gap={40}>
      <VerticalSpacer gap={16}>
        <CSSBreakpoint min="tablet">
          <VerticalSpacer gap={8}>
            <Heading variant="heading3" align="center" as="pre">{competitionHeader}</Heading>
            <Caption variant="large" align="center" colour="neutral-two">{bodyText}</Caption>
          </VerticalSpacer>
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <VerticalSpacer gap={8}>
            <Heading variant="heading3" align="start">{competitionHeader}</Heading>
            <Caption variant="large" align="start" colour="neutral-two">{bodyText}</Caption>
          </VerticalSpacer>
        </CSSBreakpoint>
      </VerticalSpacer>
      {isRewardReceived && isModalOpen && <ConfettiWrapper>
        <ConfettiExplosion force={0.75} duration={5000} particleCount={200} height={3000} />
      </ConfettiWrapper>}
      <WheelOfPrize
        prize={prize}
        drawDate={drawDate}
        promoLink={promo_link}
        fetchPrize={fetchPrize}
        competitionTitle={competitionTitle}
        breadcrumb={breadcrumb}
        setAnimationFinished={setAnimationFinished}
        isAnimationFinished={isAnimationFinished}
        memberId={memberId}
        isModalOpen={isModalOpen}
        closeModal={closeModal}
      />
      {footerText && (
        <BodyText variant="medium" align="center">
          <FormattedMessage
            id="AR-OpG0DmQzilhssuNEl"
            defaultMessage="{footerText} <link>Full terms and conditions apply</link>"
            description="Competition footer text with link to full terms and conditions"
            values={{
              footerText,
              link: (chunks) => <TextLink to="/promotions">{chunks}</TextLink>,
            }}
          />
        </BodyText>
      )}
    </Root>
  )
}

export default Competition
